export class StateManager {
    // Estados operativos basados en la respuesta del backend
    static ESTADOS_OPERATIVOS = {
        CARGUE: 'CARGUE',
        EN_RUTA: 'EN RUTA',
        DESCARGUE: 'DESCARGUE',
        COMPLETADO: 'COMPLETADO'
    };

    // Eventos disponibles
    static EVENTOS = {
        LLEGADA: 'LLEGADA',
        SALIDA: 'SALIDA'
    };

    // Fases
    static FASES = {
        CARGUE: 'CARGUE',
        DESCARGUE: 'DESCARGUE'
    };

    // Estados de eventos
    static ESTADOS_EVENTO = {
        PENDIENTE: 'PENDIENTE',
        REPORTADO: 'REPORTADO',
        BLOQUEADO: 'BLOQUEADO'
    };

    // NUEVO: Mapeo de eventos a campos de fecha
    static CAMPOS_FECHA = {
        [this.FASES.CARGUE]: {
            [this.EVENTOS.LLEGADA]: 'fecha_llegada_cargue',
            [this.EVENTOS.SALIDA]: 'fecha_salida_cargue'
        },
        [this.FASES.DESCARGUE]: {
            [this.EVENTOS.LLEGADA]: 'fecha_llegada_descargue',
            [this.EVENTOS.SALIDA]: 'fecha_salida_descargue'
        }
    };

    // Crea estructura vacía de eventos
    static crearEventosVacios() {
        return {
            [this.EVENTOS.LLEGADA]: { estado: this.ESTADOS_EVENTO.BLOQUEADO, timestamp: null },
            [this.EVENTOS.SALIDA]: { estado: this.ESTADOS_EVENTO.BLOQUEADO, timestamp: null }
        };
    };
    
    // Forzar transición a DESCARGUE (solo para UI, no persiste en BD)
    static forzarTransicionADescargue(operacionData) {
        if (!operacionData || !operacionData.remesas || operacionData.remesas.length === 0) {
            return null;
        }

        // Validar que se puede hacer la transición
        const estadoActual = this.determinarEstadoOperativo(operacionData);

        // Solo permitir desde EN_RUTA
        if (estadoActual !== this.ESTADOS_OPERATIVOS.EN_RUTA) {
            console.warn('No se puede transicionar a DESCARGUE desde', estadoActual);
            return null;
        }

        // Clonar operacionData y simular que tiene fecha_llegada_descargue
        const operacionModificada = {
            ...operacionData,
            remesas: operacionData.remesas.map(remesa => ({
                ...remesa,
                // Agregar flag temporal para simular llegada a descargue
                _transicion_descargue_forzada: true
            }))
        };

        return operacionModificada;
    };

    // Determina el estado operativo basado en los flags del backend
    static determinarEstadoOperativo(operacionData) {
        if (!operacionData || !operacionData.remesas || operacionData.remesas.length === 0) {
            return this.ESTADOS_OPERATIVOS.CARGUE;
        }

        // Agrupar remesas por ruta
        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        
        // Si todas las rutas están completadas
        const todasCompletadas = gruposRuta.every(grupo => 
            grupo.remesas.every(r => r.fecha_salida_descargue)
        );
        if (todasCompletadas) return this.ESTADOS_OPERATIVOS.COMPLETADO;

        // Si alguna ruta está en DESCARGUE
        const algunaEnDescargue = gruposRuta.some(grupo =>
            grupo.remesas.some(r => r.fecha_llegada_descargue && !r.fecha_salida_descargue)
        );
        if (algunaEnDescargue) return this.ESTADOS_OPERATIVOS.DESCARGUE;

        // Si alguna ruta está EN_RUTA
        const algunaEnRuta = gruposRuta.some(grupo =>
            grupo.remesas.some(r => r.fecha_salida_cargue && !r.fecha_llegada_descargue)
        );
        if (algunaEnRuta) return this.ESTADOS_OPERATIVOS.EN_RUTA;

        // Por defecto, CARGUE
        return this.ESTADOS_OPERATIVOS.CARGUE;
    };

    // Agrupa remesas por ruta (origen_dane + destino_dane)
    static agruparRemesasPorRuta(remesas) {
        if (!remesas || !Array.isArray(remesas)) return [];

        const grupos = {};

        remesas.forEach(remesa => {
            const claveRuta = `${remesa.origen_dane}_${remesa.destino_dane}`;
            
            if (!grupos[claveRuta]) {
                grupos[claveRuta] = {
                    origen: remesa.origen,
                    destino: remesa.destino,
                    origen_dane: remesa.origen_dane,
                    destino_dane: remesa.destino_dane,
                    remesas: []
                };
            }
            
            grupos[claveRuta].remesas.push(remesa);
        });

        return Object.values(grupos).sort((a, b) => 
            a.origen.localeCompare(b.origen, 'es', { sensitivity: 'base' })
        );
    };

    // Obtener estado detallado considerando remesas individuales
    static obtenerEstadoDetalladoPorEvento(fase, evento, operacionData) {
        if (!operacionData?.remesas?.length) {
            return {
                estado: this.ESTADOS_EVENTO.BLOQUEADO,
                gruposReportados: [],
                gruposPendientes: [],
                totalGrupos: 0,
                progreso: 0,
                remesasPorGrupo: {}
            };
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        const gruposReportados = [];
        const gruposPendientes = [];
        const remesasPorGrupo = {}; 

        gruposRuta.forEach(grupo => {
            const grupoId = `${grupo.origen_dane}_${grupo.destino_dane}`;
            const grupoInfo = {
                id: grupoId,
                origen: grupo.origen,
                destino: grupo.destino,
                cantidadRemesas: grupo.remesas.length
            };

            // Analizar cada remesa individualmente
            const estadosRemesas = grupo.remesas.map(remesa => {
                const estadoRemesa = this.obtenerEstadoRemesaIndividual(remesa, fase, evento);
                return {
                    id: remesa.id,
                    id_remesa: remesa.id_remesa,
                    consecutivo: remesa.consecutivo || remesa.id_remesa,
                    estado: estadoRemesa.estado,
                    razon: estadoRemesa.razon,
                    timestamp: estadoRemesa.timestamp
                };
            });

            // Guardar estados de remesas para este grupo
            remesasPorGrupo[grupoId] = estadosRemesas;

            // Contar remesas por estado
            const remesasReportadas = estadosRemesas.filter(r => 
                r.estado === this.ESTADOS_EVENTO.REPORTADO
            ).length;
            
            const remesasPendientes = estadosRemesas.filter(r => 
                r.estado === this.ESTADOS_EVENTO.PENDIENTE
            ).length;

            const remesasBloqueadas = estadosRemesas.filter(r => 
                r.estado === this.ESTADOS_EVENTO.BLOQUEADO
            ).length;

            // Determinar estado del grupo
            // Caso 1: TODAS las remesas reportaron el evento -> GRUPO REPORTADO
            if (remesasReportadas === grupo.remesas.length) {
                gruposReportados.push({
                    ...grupoInfo,
                    timestamp: estadosRemesas.find(r => r.timestamp)?.timestamp,
                    remesasReportadas: remesasReportadas,
                    remesasPendientes: 0,
                    todasReportadas: true
                });
            } 
            // Caso 2: AL MENOS UNA remesa puede reportar (PENDIENTE) -> GRUPO PENDIENTE
            else if (remesasPendientes > 0) {
                gruposPendientes.push({
                    ...grupoInfo,
                    remesasReportadas: remesasReportadas,
                    remesasPendientes: remesasPendientes,
                    esMixto: remesasReportadas > 0, // Algunas reportadas, otras pendientes
                    detalleRemesas: estadosRemesas.filter(r => 
                        r.estado === this.ESTADOS_EVENTO.PENDIENTE
                    ).map(r => r.consecutivo || `#${r.id_remesa}`)
                });
            } 
            // Caso 3: TODAS bloqueadas o parcialmente reportadas sin pendientes
            else if (remesasBloqueadas > 0 || remesasReportadas > 0) {
                // Si hay remesas reportadas pero ninguna pendiente, 
                // significa que las demás están completadas globalmente
                // Este grupo NO debe aparecer en listas, ya que no tiene acciones disponibles
                if (remesasReportadas > 0 && remesasReportadas < grupo.remesas.length) {
                    console.warn(`Grupo ${grupoId}: Estado inconsistente para evento ${evento} ${fase}`, {
                        reportadas: remesasReportadas,
                        pendientes: remesasPendientes,
                        bloqueadas: remesasBloqueadas,
                        total: grupo.remesas.length,
                        estadosRemesas
                    });
                }
            }
        });

        const totalGrupos = gruposRuta.length;
        const progreso = gruposReportados.length / totalGrupos;

        // Determinar estado global del evento
        let estadoGlobal;
        if (gruposReportados.length === totalGrupos) {
            // Todos reportaron
            estadoGlobal = this.ESTADOS_EVENTO.REPORTADO;
        } else if (gruposPendientes.length > 0) {
            estadoGlobal = this.ESTADOS_EVENTO.PENDIENTE;
        } else {
            estadoGlobal = this.ESTADOS_EVENTO.BLOQUEADO;
        }

        return {
            estado: estadoGlobal,
            gruposReportados,
            gruposPendientes,
            totalGrupos,
            progreso,
            esMixto: gruposReportados.length > 0 && gruposPendientes.length > 0,
            remesasPorGrupo
        };
    };

    // Verifica si un grupo específico puede reportar un evento
    static gruposPuedeReportarEvento(grupo, evento, fase) {
        const eventosOrdenMismaFase = [this.EVENTOS.LLEGADA, this.EVENTOS.SALIDA];
        const camposFecha = this.CAMPOS_FECHA[fase];

        const indiceEvento = eventosOrdenMismaFase.indexOf(evento);
        if (indiceEvento === -1) return false;

        const isReported = (val) => val !== null && val !== undefined && String(val).trim() !== '';

        // Excluir remesas ya completadas (no deben permitir nuevos reportes)
        const remesasActivas = grupo.remesas.filter(r => !String(r.fecha_salida_descargue || '').trim());
        if (remesasActivas.length === 0) return false;

        if (fase === this.FASES.CARGUE) {
            const campoActual = camposFecha[evento];

            if (evento === this.EVENTOS.LLEGADA) {
                // Puede reportar LLEGADA CARGUE si existe al menos una remesa activa sin fecha_llegada_cargue
                return remesasActivas.some(remesa => !isReported(remesa[campoActual]));
            }

            if (evento === this.EVENTOS.SALIDA) {
                // Puede reportar SALIDA CARGUE si existe al menos una remesa activa con llegada_cargue reportada y sin salida_cargue
                const campoLlegadaCargue = this.CAMPOS_FECHA[this.FASES.CARGUE][this.EVENTOS.LLEGADA];
                const campoSalidaCargue = this.CAMPOS_FECHA[this.FASES.CARGUE][this.EVENTOS.SALIDA];
                return remesasActivas.some(remesa => isReported(remesa[campoLlegadaCargue]) && !isReported(remesa[campoSalidaCargue]));
            }
        } else if (fase === this.FASES.DESCARGUE) {
            const campoLlegadaDescargue = this.CAMPOS_FECHA[this.FASES.DESCARGUE][this.EVENTOS.LLEGADA];
            const campoSalidaDescargue = this.CAMPOS_FECHA[this.FASES.DESCARGUE][this.EVENTOS.SALIDA];

            if (evento === this.EVENTOS.LLEGADA) {
                // Llegada Descargue sólo si la remesa ya salió de cargue (fecha_salida_cargue) y no tiene llegada_descargue
                const campoSalidaCargue = this.CAMPOS_FECHA[this.FASES.CARGUE][this.EVENTOS.SALIDA];
                return remesasActivas.some(remesa => isReported(remesa[campoSalidaCargue]) && !isReported(remesa[campoLlegadaDescargue]));
            }

            if (evento === this.EVENTOS.SALIDA) {
                // Salida Descargue sólo si la remesa ya tiene llegada_descargue y no tiene salida_descargue
                return remesasActivas.some(remesa => isReported(remesa[campoLlegadaDescargue]) && !isReported(remesa[campoSalidaDescargue]));
            }
        }

        return false;
    };

    static obtenerProximosEventosPorGrupo(fase, operacionData) {
        if (!operacionData?.remesas?.length) return [];
        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        const eventosOrden = [this.EVENTOS.LLEGADA, this.EVENTOS.SALIDA];
        const camposFecha = this.CAMPOS_FECHA[fase];
        
        return gruposRuta.map(grupo => {
            const remesa = grupo.remesas[0];
            const eventosReportados = [];

            eventosOrden.forEach(evento => {
                const campoFecha = camposFecha[evento];
                if (remesa[campoFecha]?.trim()) {
                    eventosReportados.push(evento);
                }
            });

            const proximoEvento = this.obtenerProximoEventoPendiente(eventosReportados, eventosOrden);

            return {
                grupoId: `${grupo.origen_dane}_${grupo.destino_dane}`,
                origen: grupo.origen,
                destino: grupo.destino,
                proximoEvento,
                eventosReportados,
                totalEventos: eventosOrden.length,
                progreso: eventosReportados.length / eventosOrden.length
            };
        }).filter(info => info.proximoEvento !== null); // Solo grupos con eventos pendientes
    }

    // Obtiene el estado de los eventos para una fase específica
    static obtenerEstadosEventos(fase, operacionData) {
        if (!operacionData || !operacionData.remesas || operacionData.remesas.length === 0) {
            return this.crearEventosVacios();
        }

        // Si NINGÚN tiempo está reportado en el manifiesto, activar por defecto LLEGADA en CARGUE
        const anyDateReported = operacionData.remesas.some(r => {
            return (
                (r.fecha_llegada_cargue && String(r.fecha_llegada_cargue).trim() !== '') ||
                (r.fecha_salida_cargue && String(r.fecha_salida_cargue).trim() !== '') ||
                (r.fecha_llegada_descargue && String(r.fecha_llegada_descargue).trim() !== '') ||
                (r.fecha_salida_descargue && String(r.fecha_salida_descargue).trim() !== '')
            );
        });

        const eventos = {};
        const eventosOrden = [this.EVENTOS.LLEGADA, this.EVENTOS.SALIDA];

        // Caso inicial: ningún tiempo reportado -> si estoy en fase CARGUE, LLEGADA pendiente, SALIDA bloqueada
        if (!anyDateReported) {
            if (fase === this.FASES.CARGUE) {
                eventos[this.EVENTOS.LLEGADA] = {
                    estado: this.ESTADOS_EVENTO.PENDIENTE,
                    gruposReportados: [],
                    gruposPendientes: this.agruparRemesasPorRuta(operacionData.remesas).map(g => ({
                        id: `${g.origen_dane}_${g.destino_dane}`,
                        origen: g.origen,
                        destino: g.destino,
                        cantidadRemesas: g.remesas.length
                    })),
                    totalGrupos: this.agruparRemesasPorRuta(operacionData.remesas).length,
                    progreso: 0
                };
                eventos[this.EVENTOS.SALIDA] = this.crearEventosVacios()[this.EVENTOS.SALIDA];
                return eventos;
            } else {
                // En fases diferentes a CARGUE, bloquear ambos
                return this.crearEventosVacios();
            }
        }

        // En caso normal, construir cada evento usando obtenerEstadoDetalladoPorEvento
        eventosOrden.forEach(evento => {
            eventos[evento] = this.obtenerEstadoDetalladoPorEvento(
                fase,
                evento,
                operacionData
            );
        });

        return eventos;
    };

    // Obtener timestamp del primer grupo que reportó
    static obtenerTimestampDeGrupos(evento, fase, operacionData) {
        const camposFecha = this.CAMPOS_FECHA[fase];
        const campoFecha = camposFecha[evento];
        
        for (const remesa of operacionData.remesas) {
            if (remesa[campoFecha]?.trim()) {
                return remesa[campoFecha];
            }
        }
        
        return null;
    }
    
    // Listar grupos que reportaron un evento
    static obtenerGruposQueReportaron(evento, fase, operacionData) {
        const camposFecha = this.CAMPOS_FECHA[fase];
        const campoFecha = camposFecha[evento];
        const grupos = [];
        
        operacionData.remesas.forEach(remesa => {
            if (remesa[campoFecha]?.trim()) {
                grupos.push(`${remesa.origen} → ${remesa.destino}`);
            }
        });
        
        return [...new Set(grupos)]; 
    }

    // Determina si un evento puede ser reportado basado en la secuencia
    static puedeReportarEventoPorSecuencia(evento, ultimoEventoReportado) {
        const eventosOrden = [this.EVENTOS.LLEGADA, this.EVENTOS.SALIDA];
        const indiceEvento = eventosOrden.indexOf(evento);
        
        if (indiceEvento === -1) return false;

        // El primer evento siempre puede ser reportado
        if (indiceEvento === 0) return true;

        // Para otros eventos, verificar que el anterior esté reportado
        const eventoAnterior = eventosOrden[indiceEvento - 1];
        return ultimoEventoReportado === eventoAnterior;
    };

    // Valida si se puede reportar un evento específico
    static puedeReportarEvento(fase, evento, operacionData) {
        const eventos = this.obtenerEstadosEventos(fase, operacionData);
        const estadoEvento = eventos[evento];
        return estadoEvento && estadoEvento.estado === this.ESTADOS_EVENTO.PENDIENTE;
    };

    // Obtiene el próximo evento que puede ser reportado
    static obtenerProximoEventoPendiente(eventosReportados, eventosOrden) {
        // Si no hay eventos reportados, el primero está pendiente
        if (eventosReportados.length === 0) {
            return eventosOrden[0];
        }

        // Si todos los eventos están reportados, no hay próximo
        if (eventosReportados.length >= eventosOrden.length) {
            return null;
        }

        // Encontrar el próximo evento en secuencia
        for (let i = 0; i < eventosOrden.length; i++) {
            const evento = eventosOrden[i];
            if (!eventosReportados.includes(evento)) {
                // Verificar que todos los eventos anteriores estén reportados
                const eventosAnteriores = eventosOrden.slice(0, i);
                const todosAnterioresReportados = eventosAnteriores.every(eventoAnterior => 
                    eventosReportados.includes(eventoAnterior)
                );
                
                if (todosAnterioresReportados) {
                    return evento;
                } else {
                    // Si faltan eventos anteriores, no se puede habilitar este
                    return null;
                }
            }
        }

        return null;
    };

    // Obtiene el progreso general del manifiesto
    static calcularProgresoManifiesto(operacionData) {
        if (!operacionData || !operacionData.remesas || operacionData.remesas.length === 0) {
            return 0;
        }

        const remesa = operacionData.remesas[0];
        const totalEventos = 4; // 2 para cargue + 2 para descargue
        let eventosCompletados = 0;

        // Contar eventos de cargue
        if (remesa.fecha_llegada_cargue) eventosCompletados++;
        if (remesa.fecha_salida_cargue) eventosCompletados++;

        // Contar eventos de descargue
        if (remesa.fecha_llegada_salida) eventosCompletados++;
        if (remesa.fecha_salida_salida) eventosCompletados++;

        return eventosCompletados / totalEventos;
    };

    // Obtiene información detallada del estado actual
    static obtenerInformacionEstado(operacionData) {
        const estadoOperativo = this.determinarEstadoOperativo(operacionData);
        const progreso = this.calcularProgresoManifiesto(operacionData);
        
        let faseActual = this.FASES.CARGUE;
        let proximoEvento = this.EVENTOS.LLEGADA;

        if (estadoOperativo === this.ESTADOS_OPERATIVOS.DESCARGUE) {
            faseActual = this.FASES.DESCARGUE;
            const eventosDescargue = this.obtenerEstadosEventos(this.FASES.DESCARGUE, operacionData);
            proximoEvento = this.obtenerProximoEventoPendienteDesdeEstados(eventosDescargue);
        } else if (estadoOperativo === this.ESTADOS_OPERATIVOS.CARGUE) {
            const eventosCargue = this.obtenerEstadosEventos(this.FASES.CARGUE, operacionData);
            proximoEvento = this.obtenerProximoEventoPendienteDesdeEstados(eventosCargue);
        }

        return {
            estadoOperativo,
            faseActual,
            proximoEvento,
            progreso,
            puedeAvanzar: progreso < 1
        };
    };

    // Método para obtener próximo evento desde objeto de estados
    static obtenerProximoEventoPendienteDesdeEstados(estadosEventos) {
        if (!estadosEventos || typeof estadosEventos !== 'object') {
            return null;
        }

        const eventosOrden = [this.EVENTOS.LLEGADA, this.EVENTOS.SALIDA];
        
        for (const evento of eventosOrden) {
            if (estadosEventos[evento]?.estado === this.ESTADOS_EVENTO.PENDIENTE) {
                return evento;
            }
        }

        return null;
    };

    // Verifica si hay grupos con eventos pendientes en CARGUE
    static tieneGruposPendientesEnCargue(operacionData) {
        if (!operacionData?.remesas?.length) {
            return false;
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        
        // Verificar si algún grupo NO ha completado todos los eventos de CARGUE
        return gruposRuta.some(grupo => {
            // Excluir remesas ya completadas del análisis
            const remesasActivas = grupo.remesas.filter(r => !String(r.fecha_salida_descargue || '').trim());
            if (remesasActivas.length === 0) return false;

            // Un grupo tiene eventos pendientes en CARGUE si al menos una remesa activa falta llegada o salida
            const faltaLlegada = remesasActivas.some(r => !String(r.fecha_llegada_cargue || '').trim());
            const faltaSalida = remesasActivas.some(r => !String(r.fecha_salida_cargue || '').trim());
            
            return faltaLlegada || faltaSalida;
        });
    };

    // Verifica si hay grupos con eventos pendientes en DESCARGUE
    static tieneGruposPendientesEnDescargue(operacionData) {
        if (!operacionData?.remesas?.length) {
            return false;
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        
        // Verificar si algún grupo NO ha completado todos los eventos de DESCARGUE
        return gruposRuta.some(grupo => {
            // Excluir remesas ya completadas del análisis
            const remesasActivas = grupo.remesas.filter(r => !String(r.fecha_salida_descargue || '').trim());
            if (remesasActivas.length === 0) return false;

            // Solo verificar descargue si ya llegó alguna remesa activa
            const algunaLlegada = remesasActivas.some(r => String(r.fecha_llegada_descargue || '').trim());
            if (!algunaLlegada) return false;

            // Si llegó pero falta salida en alguna remesa activa -> pendiente
            const faltaSalida = remesasActivas.some(r => !String(r.fecha_salida_descargue || '').trim());
            return faltaSalida;
        });
    };

    // Verifica si hay grupos pendientes de reportar LLEGADA DESCARGUE
    static tieneGruposPendientesLlegadaDescargue(operacionData) {
        if (!operacionData?.remesas?.length) {
            return false;
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        
        // Verificar si algún grupo NO ha reportado LLEGADA DESCARGUE en remesas activas
        return gruposRuta.some(grupo => {
            const remesasActivas = grupo.remesas.filter(r => !String(r.fecha_salida_descargue || '').trim());
            if (remesasActivas.length === 0) return false;
            return remesasActivas.some(r => !String(r.fecha_llegada_descargue || '').trim());
        });
    };

    // Obtiene estadísticas detalladas de progreso por fase
    static obtenerEstadisticasPorFase(operacionData) {
        if (!operacionData?.remesas?.length) {
            return {
                cargue: { completados: 0, pendientes: 0, total: 0 },
                descargue: { completados: 0, pendientes: 0, total: 0 }
            };
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        const totalGrupos = gruposRuta.length;

        let gruposCompletoCargue = 0;
        let gruposCompletoDescargue = 0;

        gruposRuta.forEach(grupo => {
            // Remesas activas para cálculos de cargue/descargue
            const remesasActivas = grupo.remesas.filter(r => !String(r.fecha_salida_descargue || '').trim());

            // CARGUE: si no hay remesas activas -> considerar completado
            if (remesasActivas.length === 0) {
                gruposCompletoCargue++;
            } else {
                const completoCargue = remesasActivas.every(r =>
                    String(r.fecha_llegada_cargue || '').trim() &&
                    String(r.fecha_salida_cargue || '').trim()
                );
                if (completoCargue) gruposCompletoCargue++;
            }

            // DESCARGUE: grupo completado si todas las remesas (no solo activas) ya tienen salida_descargue
            const completoDescargue = grupo.remesas.every(r => String(r.fecha_salida_descargue || '').trim());
            if (completoDescargue) gruposCompletoDescargue++;
        });

        return {
            cargue: {
                completados: gruposCompletoCargue,
                pendientes: totalGrupos - gruposCompletoCargue,
                total: totalGrupos,
                porcentaje: Math.round((gruposCompletoCargue / totalGrupos) * 100)
            },
            descargue: {
                completados: gruposCompletoDescargue,
                pendientes: totalGrupos - gruposCompletoDescargue,
                total: totalGrupos,
                porcentaje: Math.round((gruposCompletoDescargue / totalGrupos) * 100)
            }
        };
    };

    // Verifica si hay al menos un grupo completado en CARGUE y otros pendientes
    static tieneGruposCompletosYPendientesEnCargue(operacionData) {
        if (!operacionData?.remesas?.length) {
            return false;
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        
        let gruposCompletos = 0;
        let gruposPendientes = 0;

        gruposRuta.forEach(grupo => {
            // Remesas activas para determinar completitud en cargue
            const remesasActivas = grupo.remesas.filter(r => !String(r.fecha_salida_descargue || '').trim());

            // Si no hay remesas activas => grupo ya completado
            if (remesasActivas.length === 0) return;

            const estaCompleto = remesasActivas.every(r =>
                String(r.fecha_llegada_cargue || '').trim() &&
                String(r.fecha_salida_cargue || '').trim()
            );

            if (estaCompleto) {
                gruposCompletos++;
            } else {
                gruposPendientes++;
            }
        });

        // Retorna true si hay AL MENOS 1 completo Y AL MENOS 1 pendiente
        return gruposCompletos > 0 && gruposPendientes > 0;
    };

    // MÉTODOS PARA EL TIMELINEMODAL

    // Calcula el progreso global del manifiesto considerando TODAS las remesas
    static calcularProgresoGlobalManifiesto(operacionData) {
        if (!operacionData?.remesas?.length) {
            return 0;
        }

        const totalRemesas = operacionData.remesas.length;
        const eventosPorRemesa = 4; // 2 cargue + 2 descargue
        const totalEventosPosibles = totalRemesas * eventosPorRemesa;

        let eventosReportados = 0;

        operacionData.remesas.forEach(remesa => {
            // Eventos de CARGUE
            if (remesa.fecha_llegada_cargue?.trim()) eventosReportados++;
            if (remesa.fecha_salida_cargue?.trim()) eventosReportados++;

            // Eventos de DESCARGUE
            if (remesa.fecha_llegada_descargue?.trim()) eventosReportados++;
            if (remesa.fecha_salida_descargue?.trim()) eventosReportados++;
        });

        return eventosReportados / totalEventosPosibles;
    };

    // Agrupa eventos del timeline por grupo de remesas
    // Retorna estructura organizada para mostrar en la modal
    static agruparEventosPorGrupoRemesas(operacionData) {
        if (!operacionData?.remesas?.length) {
            return [];
        }

        // Agrupar remesas por ruta
        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        
        const eventosAgrupados = gruposRuta.map(grupo => {
            const eventosGrupo = [];
            const remesaReferencia = grupo.remesas[0];

            // Definir eventos en orden cronológico
            const eventosOrden = [
                { 
                    fase: this.FASES.CARGUE, 
                    evento: this.EVENTOS.LLEGADA, 
                    campo: 'fecha_llegada_cargue' 
                },
                { 
                    fase: this.FASES.CARGUE, 
                    evento: this.EVENTOS.SALIDA, 
                    campo: 'fecha_salida_cargue' 
                },
                { 
                    fase: this.FASES.DESCARGUE, 
                    evento: this.EVENTOS.LLEGADA, 
                    campo: 'fecha_llegada_descargue' 
                },
                { 
                    fase: this.FASES.DESCARGUE, 
                    evento: this.EVENTOS.SALIDA, 
                    campo: 'fecha_salida_descargue' 
                }
            ];

            // Extraer eventos reportados
            eventosOrden.forEach(eventoConfig => {
                const timestamp = remesaReferencia[eventoConfig.campo]?.trim();
                
                if (timestamp) {
                    const remesasDetalles = grupo.remesas.map(remesa => ({
                        consecutivo: remesa.consecutivo || remesa.numero_remesa,
                        producto: remesa.nombre_producto,
                        cliente: remesa.nombre_tercero || remesa.cliente,
                        unidades: remesa.unidades || remesa.cantidad || 0
                    }));
                    eventosGrupo.push({
                        fase: eventoConfig.fase,
                        evento: eventoConfig.evento,
                        timestamp,
                        cantidadRemesas: grupo.remesas.length,
                        remesas: remesasDetalles
                    });
                }
            });

            const remesasInfo = grupo.remesas.map(remesa => ({
                consecutivo: remesa.consecutivo || remesa.numero_remesa,
                producto: remesa.nombre_producto,
                cliente: remesa.nombre_tercero || remesa.cliente
            }));

            return {
                grupoId: `${grupo.origen_dane}_${grupo.destino_dane}`,
                origen: grupo.origen,
                destino: grupo.destino,
                cantidadRemesas: grupo.remesas.length,
                remesas: remesasInfo,
                eventos: eventosGrupo,
                progresoGrupo: eventosGrupo.length / 4 // 4 eventos totales por grupo
            };
        });

        return eventosAgrupados;
    };

    static calcularKPIsGlobales(operacionData) {
        if (!operacionData?.remesas?.length) {
            return {
                espera_min: 0,
                operacion_min: 0,
                estadia_min: 0
            };
        }

        const gruposRuta = this.agruparRemesasPorRuta(operacionData.remesas);
        let totalOperacion = 0;
        let totalEstadia = 0;
        let gruposConDatos = 0;

        gruposRuta.forEach(grupo => {
            const remesa = grupo.remesas[0];

            // Calcular tiempo de operación (LLEGADA a SALIDA de cargue)
            if (remesa.fecha_llegada_cargue && remesa.fecha_salida_cargue) {
                const llegada = new Date(remesa.fecha_llegada_cargue);
                const salida = new Date(remesa.fecha_salida_cargue);
                totalOperacion += (salida - llegada) / (1000 * 60);
            }

            if (remesa.fecha_llegada_cargue && remesa.fecha_salida_cargue) {
                const llegada = new Date(remesa.fecha_llegada_cargue);
                const salida = new Date(remesa.fecha_salida_cargue);
                totalEstadia += (salida - llegada) / (1000 * 60);
                gruposConDatos++;
            }
        });

        return {
            espera_min: 0, // Ya no aplica sin ENTRADA
            operacion_min: gruposConDatos > 0 ? Math.round(totalOperacion / gruposConDatos) : 0,
            estadia_min: gruposConDatos > 0 ? Math.round(totalEstadia / gruposConDatos) : 0
        };
    };

    // Determina el estado operativo de UN GRUPO ESPECÍFICO de remesas
    static determinarEstadoOperativoGrupo(grupoRemesas) {
        if (!grupoRemesas || !grupoRemesas.remesas || grupoRemesas.remesas.length === 0) {
            return this.ESTADOS_OPERATIVOS.CARGUE;
        }

        // Tomar la primera remesa como referencia (todas en el grupo comparten timestamps)
        const remesa = grupoRemesas.remesas[0];
        
        // Helper para validar fechas
        const esReportado = (fecha) => fecha !== null && fecha !== undefined && String(fecha).trim() !== '';

        // COMPLETADO: tiene fecha_salida_descargue
        if (esReportado(remesa.fecha_salida_descargue)) {
            return this.ESTADOS_OPERATIVOS.COMPLETADO;
        }

        // DESCARGUE: tiene fecha_llegada_descargue pero no salida_descargue
        if (esReportado(remesa.fecha_llegada_descargue)) {
            return this.ESTADOS_OPERATIVOS.DESCARGUE;
        }

        // EN_RUTA: tiene fecha_salida_cargue pero no llegada_descargue
        if (esReportado(remesa.fecha_salida_cargue)) {
            return this.ESTADOS_OPERATIVOS.EN_RUTA;
        }

        // CARGUE: por defecto o si solo tiene fecha_llegada_cargue
        return this.ESTADOS_OPERATIVOS.CARGUE;
    };

    // Método para obtener el estado de una remesa individual
    static obtenerEstadoRemesaIndividual(remesa, fase, evento) {
        const camposFecha = this.CAMPOS_FECHA[fase];
        const campoFecha = camposFecha[evento];
        
        // Helper para validar fechas reportadas
        const esReportado = (fecha) => fecha !== null && fecha !== undefined && String(fecha).trim() !== '';
        
        // Prioridad 1: Verificar si la remesa está completada globalmente
        const estaCompletada = this.esRemesaCompletada(remesa);
        if (estaCompletada) {
            return {
                estado: this.ESTADOS_EVENTO.REPORTADO,
                razon: 'COMPLETADA_GLOBAL',
                timestamp: remesa.fecha_salida_descargue
            };
        }
        
        // Prioridad 2: Verificar si el evento específico ya fue reportado
        const eventoReportado = esReportado(remesa[campoFecha]);
        if (eventoReportado) {
            return {
                estado: this.ESTADOS_EVENTO.REPORTADO,
                razon: 'EVENTO_REPORTADO',
                timestamp: remesa[campoFecha]
            };
        }
        
        // Prioridad 3: Verificar si puede reportar según las dependencias
        const puedeReportar = this.remesaPuedeReportarEvento(remesa, evento, fase);
        
        return {
            estado: puedeReportar ? this.ESTADOS_EVENTO.PENDIENTE : this.ESTADOS_EVENTO.BLOQUEADO,
            razon: puedeReportar ? 'DISPONIBLE_PARA_REPORTE' : 'BLOQUEADO_POR_DEPENDENCIAS',
            timestamp: null
        };
    };

    // Verificar si una remesa está completada
    static esRemesaCompletada(remesa) {
        return remesa.fecha_salida_descargue && 
            String(remesa.fecha_salida_descargue).trim() !== '';
    };

    // Verificar si una remesa puede reportar un evento específico
    static remesaPuedeReportarEvento(remesa, evento, fase) {
        const esReportado = (val) => val !== null && val !== undefined && String(val).trim() !== '';

        // Regla global: Si está completada, no puede reportar nada más
        if (this.esRemesaCompletada(remesa)) {
            return false;
        }
        
        // ========================================
        // VALIDACIONES POR FASE Y EVENTO
        // ========================================
        if (fase === this.FASES.CARGUE) {
            if (evento === this.EVENTOS.LLEGADA) {
                // Puede reportar LLEGADA CARGUE si aún no la tiene
                return !esReportado(remesa.fecha_llegada_cargue);
            }
            
            if (evento === this.EVENTOS.SALIDA) {
                // Puede reportar SALIDA CARGUE si:
                // - Ya reportó LLEGADA CARGUE
                // - NO ha reportado SALIDA CARGUE
                return esReportado(remesa.fecha_llegada_cargue) && 
                    !esReportado(remesa.fecha_salida_cargue);
            }
        }
        
        if (fase === this.FASES.DESCARGUE) {
            if (evento === this.EVENTOS.LLEGADA) {
                // Puede reportar LLEGADA DESCARGUE si:
                // - Ya completó fase CARGUE (tiene SALIDA CARGUE)
                // - NO ha reportado LLEGADA DESCARGUE
                return esReportado(remesa.fecha_salida_cargue) && 
                    !esReportado(remesa.fecha_llegada_descargue);
            }
            
            if (evento === this.EVENTOS.SALIDA) {
                // Puede reportar SALIDA DESCARGUE si:
                // - Ya reportó LLEGADA DESCARGUE
                // - NO ha reportado SALIDA DESCARGUE
                return esReportado(remesa.fecha_llegada_descargue) && 
                    !esReportado(remesa.fecha_salida_descargue);
            }
        }
        
        // Si no coincide con ninguna validación, no puede reportar
        return false;
    };
};